/**
 * VariableValueInput - shared value-input control used by both
 * InitializeVariableField and SetVariableField.
 *
 * Renders a true/false Dropdown for Boolean variables and a TokenizedInput
 * (multiline for Object / Array) for all other types, keeping the two
 * editors visually and behaviorally identical.
 */

import { makeStyles, Dropdown, Option } from '@coreai/fluentui-react';
import { ValueSegmentType, segmentsToString } from '@/types/valueSegment';
import type { ValueSegment } from '@/types/valueSegment';
import type { AgentParameterSchema } from './AgentParameterMenu';
import { TokenizedInput } from './TokenizedInput';
import { ExpressionPreview } from './ExpressionPreview';

export const VARIABLE_TYPE = {
  BOOLEAN: 'boolean',
  INTEGER: 'integer',
  FLOAT: 'float',
  STRING: 'string',
  OBJECT: 'object',
  ARRAY: 'array',
} as const;
export type VariableType = (typeof VARIABLE_TYPE)[keyof typeof VARIABLE_TYPE];

/** Coerce an arbitrary variable type string (e.g. capitalized Logic Apps
 * "Boolean" / "Integer") to the lowercase {@link VariableType} union. */
export function coerceVariableType(raw: string | undefined): VariableType {
  switch ((raw ?? '').toLowerCase()) {
    case 'boolean': return VARIABLE_TYPE.BOOLEAN;
    case 'integer': return VARIABLE_TYPE.INTEGER;
    case 'float':
    case 'number': return VARIABLE_TYPE.FLOAT;
    case 'object': return VARIABLE_TYPE.OBJECT;
    case 'array': return VARIABLE_TYPE.ARRAY;
    default: return VARIABLE_TYPE.STRING;
  }
}

/**
 * Coerce a raw string value into the JS type matching {@link VariableType}.
 * Returns the raw string unchanged when it isn't a clean literal — this
 * preserves expressions/tokens like `@variables('foo')` and lets the
 * runtime evaluate them.
 */
export function coerceVariableValue(
  type: VariableType,
  raw: string
): string | number | boolean | object {
  if (!raw) return raw;
  switch (type) {
    case VARIABLE_TYPE.BOOLEAN:
      if (raw === 'true') return true;
      if (raw === 'false') return false;
      return raw;
    case VARIABLE_TYPE.INTEGER: {
      const n = parseInt(raw, 10);
      return Number.isNaN(n) || String(n) !== raw.trim() ? raw : n;
    }
    case VARIABLE_TYPE.FLOAT: {
      const n = parseFloat(raw);
      return Number.isNaN(n) || !/^-?\d+(\.\d+)?([eE][-+]?\d+)?$/.test(raw.trim()) ? raw : n;
    }
    case VARIABLE_TYPE.OBJECT:
    case VARIABLE_TYPE.ARRAY:
      try {
        return JSON.parse(raw) as object;
      } catch {
        return raw;
      }
    default:
      return raw;
  }
}

const PLACEHOLDERS: Record<VariableType, string> = {
  [VARIABLE_TYPE.OBJECT]: 'Enter JSON object, e.g. {"key": "value"}',
  [VARIABLE_TYPE.ARRAY]: 'Enter JSON array, e.g. [1, 2, 3]',
  [VARIABLE_TYPE.INTEGER]: 'Enter an integer value',
  [VARIABLE_TYPE.FLOAT]: 'Enter a decimal number',
  [VARIABLE_TYPE.STRING]: 'Enter value',
  [VARIABLE_TYPE.BOOLEAN]: 'Select value',
};

const useStyles = makeStyles({
  wrapper: { position: 'relative' },
  booleanDropdown: { width: '100%' },
});

export interface VariableValueInputProps {
  fieldId: string;
  type: VariableType;
  value: string;
  segmentId: string;
  onChange: (value: string) => void;
  disabled?: boolean | undefined;
  showAgentParameterButton?: boolean | undefined;
  agentParameterSchema?: AgentParameterSchema | undefined;
  actionOutputs?: Record<string, unknown> | undefined;
  hasRunData?: boolean | undefined;
  isStale?: boolean | undefined;
}

export const VariableValueInput = ({
  fieldId,
  type,
  value,
  segmentId,
  onChange,
  disabled,
  showAgentParameterButton,
  agentParameterSchema,
  actionOutputs,
  hasRunData,
  isStale,
}: VariableValueInputProps) => {
  const styles = useStyles();

  if (type === VARIABLE_TYPE.BOOLEAN) {
    return (
      <Dropdown
        className={styles.booleanDropdown}
        value={value === 'true' || value === 'false' ? value : ''}
        selectedOptions={value ? [value] : []}
        onOptionSelect={(_, data) => {
          if (data.optionValue) onChange(data.optionValue);
        }}
        placeholder={PLACEHOLDERS[VARIABLE_TYPE.BOOLEAN]}
        disabled={disabled}
      >
        <Option key="true" value="true">true</Option>
        <Option key="false" value="false">false</Option>
      </Dropdown>
    );
  }

  const isJson = type === VARIABLE_TYPE.OBJECT || type === VARIABLE_TYPE.ARRAY;
  const segments: ValueSegment[] = [
    { id: segmentId, type: ValueSegmentType.LITERAL, value },
  ];

  return (
    <div
      className={styles.wrapper}
    >
      <TokenizedInput
        name={fieldId}
        value={segments}
        onChange={(next) => onChange(segmentsToString(next))}
        placeholder={PLACEHOLDERS[type]}
        disabled={disabled}
        multiline={isJson}
        showAgentParameterButton={showAgentParameterButton}
        agentParameterSchema={agentParameterSchema}
        actionOutputs={actionOutputs}
      />
      {value && (
        <ExpressionPreview
          value={value}
          actionOutputs={actionOutputs}
          hasRunData={hasRunData}
          isStale={isStale}
          mode="overlay"
        />
      )}
    </div>
  );
};
